import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';

const PushNotificationManager = () => {
  const { user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    const installInfo = checkInstallability();
    setIsIOS(installInfo.isIOS);
    setIsStandalone(installInfo.isStandalone);
    

    
    if (installInfo.isIOS && installInfo.isStandalone) {
      setStatusMessage('✅ iOS app installed correctly - notifications should be available');
    } else if (installInfo.isIOS && !installInfo.isStandalone) {
      setStatusMessage('⚠️ iOS detected but app not in standalone mode');
    } else {
      setStatusMessage('ℹ️ Desktop/Android device detected');
    }
    
    checkPushNotificationSupport();
    checkServiceWorkerSupport();
    
    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleBeforeInstallPrompt = (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later
    setInstallPrompt(e);
    setIsInstallable(true);
  };

  const checkPushNotificationSupport = () => {
    // Check basic support
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    
    // Check if running on iOS mobile devices (not desktop Safari)
    const isIOSMobile = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMacTouch = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    const isActualIOS = isIOSMobile || isMacTouch;
    
    // Check if running in standalone mode (installed to homescreen)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true;
    
    // Check if this is desktop Safari on Mac (not iOS)
    const isDesktopSafari = /Safari/.test(navigator.userAgent) && 
                           /Macintosh/.test(navigator.userAgent) && 
                           !isIOSMobile && 
                           navigator.maxTouchPoints === 0;
    

    
    // For actual iOS devices, push notifications only work when installed to homescreen (iOS 16.4+)
    if (isActualIOS) {
      const supported = hasServiceWorker && hasPushManager && isStandalone;
      setIsSupported(supported);
      console.log('iOS device detected:', { hasServiceWorker, hasPushManager, isStandalone, supported });
    } else {
      // For desktop browsers (including desktop Safari) and Android, basic check is sufficient
      const supported = hasServiceWorker && hasPushManager;
      setIsSupported(supported);
      console.log('Desktop/Android platform:', { hasServiceWorker, hasPushManager, supported });
    }
  };

  const checkServiceWorkerSupport = async () => {
    console.log('🔧 Checking Service Worker support...');
    
    // First check if Service Worker API exists
    const hasServiceWorker = 'serviceWorker' in navigator;
    console.log('Service Worker in navigator:', hasServiceWorker);
    
    if (hasServiceWorker) {
      try {
        // Try to register a service worker to test actual support
        console.log('🔧 Testing Service Worker registration...');
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          updateViaCache: 'none'
        });
        
        console.log('✅ Service Worker registration test successful:', registration);
        

        
        // Check for existing subscription
        try {
          const readyReg = await navigator.serviceWorker.ready;
          const sub = await readyReg.pushManager.getSubscription();
          setSubscription(sub);
          setIsSubscribed(!!sub);
          console.log('Existing subscription found:', !!sub);
        } catch (subError) {
          console.log('No existing subscription found:', subError.message);
        }
        
      } catch (error) {
        console.error('❌ Service Worker registration test failed:', error);
        

      }
    } else {
      console.log('❌ Service Worker not supported in this browser');

    }
  };

  const checkInstallability = () => {
    // Check if the app is already installed (running in standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true;
    
    // Check if running on actual iOS devices (not desktop Safari)
    const isIOSMobile = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isMacTouch = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    const isActualIOS = isIOSMobile || isMacTouch;
    
    if (isStandalone) {
      console.log('App is running in standalone mode (installed)');
    } else if (isActualIOS) {
      console.log('iOS device detected but app not installed to homescreen');
    }
    
    return { isStandalone, isIOS: isActualIOS };
  };

  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        console.log('🔧 Starting service worker registration...');
        
        // Check if we already have a registration
        let registration = await navigator.serviceWorker.getRegistration('/');
        
        if (!registration) {
          console.log('No existing service worker found, registering new one...');
          registration = await navigator.serviceWorker.register('/sw.js', {
            scope: '/',
            type: 'classic'
          });
          console.log('Service Worker registered successfully:', registration);
          
          // Wait for installation to complete
          if (registration.installing) {
            console.log('Service Worker installing...');
            await new Promise((resolve) => {
              registration.installing.addEventListener('statechange', () => {
                if (registration.installing.state === 'installed') {
                  console.log('Service Worker installed');
                  resolve();
                }
              });
            });
          }
        } else {
          console.log('Using existing service worker registration:', registration);
        }
        
        // Wait for the service worker to be ready
        console.log('Waiting for service worker to be ready...');
        const readyRegistration = await navigator.serviceWorker.ready;
        console.log('Service Worker is ready:', {
          scope: readyRegistration.scope,
          active: !!readyRegistration.active,
          installing: !!readyRegistration.installing,
          waiting: !!readyRegistration.waiting,
          pushManager: !!readyRegistration.pushManager
        });
        
        // Additional check for iOS
        const isIOSMobile = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOSMobile) {
          console.log('🍎 iOS detected, verifying push manager availability...');
          if (!readyRegistration.pushManager) {
            throw new Error('Push Manager not available on iOS service worker');
          }
          console.log('✅ Push Manager available on iOS');
        }
        
        return readyRegistration;
      } catch (error) {
        console.error('❌ Service Worker registration failed:', error);
        throw new Error(`Service Worker registration failed: ${error.message}`);
      }
    }
    throw new Error('Service Worker not supported in this browser');
  };

  const subscribeToPushNotifications = async () => {
    setIsLoading(true);
    setStatusMessage('🔄 Starting push notification setup...');
    
    try {
      console.log('🔔 Starting push notification subscription process...');
      
      // iOS-specific debugging
      const isIOSMobile = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isMacTouch = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
      const isActualIOS = isIOSMobile || isMacTouch;
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          window.navigator.standalone === true;
      
      console.log('🍎 iOS Debug Info:', {
        isIOSMobile,
        isMacTouch,
        isActualIOS,
        isStandalone,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        maxTouchPoints: navigator.maxTouchPoints,
        standalone: window.navigator.standalone,
        displayMode: window.matchMedia('(display-mode: standalone)').matches
      });
      
      if (isActualIOS && !isStandalone) {
        setStatusMessage('❌ App must be opened from homescreen on iOS');
        throw new Error('On iOS, push notifications only work when the app is installed to homescreen and opened from there.');
      }
      
      // Check basic support again
      setStatusMessage('🔍 Checking browser support...');
      if (!('serviceWorker' in navigator)) {
        setStatusMessage('❌ Service Worker not supported');
        throw new Error('Service Worker not supported in this browser');
      }
      
      if (!('PushManager' in window)) {
        setStatusMessage('❌ Push Manager not supported');
        throw new Error('Push Manager not supported in this browser');
      }
      
      // Check permissions first
      setStatusMessage('🔔 Requesting notification permission...');
      console.log('🔔 Requesting notification permission...');
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        console.log('Notification permission result:', permission);
        setStatusMessage(`🔔 Permission result: ${permission}`);
        
        if (permission !== 'granted') {
          setStatusMessage(`❌ Permission ${permission} - check device settings`);
          throw new Error(`Notification permission ${permission}. Please enable notifications in your device settings.`);
        }
      } else {
        setStatusMessage('❌ Notification API not available');
        throw new Error('Notification API not available');
      }
      
      // Register service worker first
      setStatusMessage('📝 Setting up service worker...');
      console.log('📝 Registering service worker...');
      const registration = await registerServiceWorker();
      console.log('✅ Service worker registered:', registration);
      setStatusMessage('✅ Service worker ready');
      
      // Wait a bit for iOS service worker to be fully ready
      if (isActualIOS) {
        console.log('🍎 Waiting for iOS service worker to be fully ready...');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Check if push manager is available on the registration
      if (!registration.pushManager) {
        throw new Error('Push Manager not available on service worker registration');
      }
      
      // Get VAPID public key from server
      setStatusMessage('🔑 Getting security key from server...');
      console.log('🔑 Getting VAPID public key from server...');
              const response = await apiClient.get('/api/push/vapid-public-key');
      console.log('VAPID key response:', response.data);
      
      if (!response.data.publicKey) {
        setStatusMessage('❌ No security key received');
        throw new Error('No VAPID public key received from server');
      }
      
      const { publicKey } = response.data;
      console.log('✅ VAPID public key received:', publicKey.substring(0, 20) + '...');
      setStatusMessage('✅ Security key received');
      
      // Convert VAPID key for iOS compatibility
      let applicationServerKey;
      try {
        applicationServerKey = urlBase64ToUint8Array(publicKey);
        console.log('✅ VAPID key converted to Uint8Array, length:', applicationServerKey.length);
      } catch (keyError) {
        console.error('❌ VAPID key conversion failed:', keyError);
        throw new Error('Invalid VAPID key format');
      }
      
      // Create subscription with iOS-specific options
      setStatusMessage('📱 Creating push subscription...');
      console.log('📱 Creating push subscription...');
      const subscriptionOptions = {
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      };
      
      console.log('Subscription options:', subscriptionOptions);
      
      const sub = await registration.pushManager.subscribe(subscriptionOptions);
      console.log('✅ Push subscription created:', {
        endpoint: sub.endpoint,
        keys: sub.toJSON().keys
      });
      setStatusMessage('✅ Push subscription created');
      
      // Send subscription to server
      setStatusMessage('📤 Saving to server...');
      console.log('📤 Sending subscription to server...');
      const subscriptionData = sub.toJSON();
      console.log('Subscription data to send:', subscriptionData);
      
              await apiClient.post('/api/push/subscribe', subscriptionData);
      console.log('✅ Subscription saved to server');
      setStatusMessage('✅ Notifications enabled successfully!');
      
      setSubscription(sub);
      setIsSubscribed(true);
      
      console.log('✅ Successfully subscribed to push notifications');
      alert('Push notifications enabled! You\'ll receive notifications when schedules are updated or important announcements are made.');
      
    } catch (error) {
      console.error('❌ Push subscription failed:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        response: error.response?.data
      });
      
      // Provide more specific error messages
      let errorMessage = 'Failed to enable push notifications. ';
      let statusError = '❌ ';
      
      if (error.message.includes('homescreen')) {
        errorMessage += 'Please make sure you opened this app from your homescreen (not from Safari directly).';
        statusError += 'Must be opened from homescreen';
      } else if (error.message.includes('permission')) {
        errorMessage += 'Please enable notifications in your device Settings > Notifications > [App Name].';
        statusError += 'Permission denied - check device settings';
      } else if (error.message.includes('not supported')) {
        errorMessage += 'Push notifications are not supported on this device/browser.';
        statusError += 'Not supported on this device';
      } else if (error.name === 'NotAllowedError') {
        errorMessage += 'Notification permission was denied. Please enable notifications in Settings.';
        statusError += 'Permission denied';
      } else if (error.name === 'AbortError') {
        errorMessage += 'The subscription process was interrupted. Please try again.';
        statusError += 'Process interrupted';
      } else if (error.name === 'NotSupportedError') {
        errorMessage += 'Push notifications are not supported on this device.';
        statusError += 'Not supported';
      } else if (error.response?.status === 401) {
        errorMessage += 'Authentication failed. Please log in again.';
        statusError += 'Authentication failed';
      } else if (error.response?.data?.error) {
        errorMessage += error.response.data.error;
        statusError += error.response.data.error;
      } else {
        errorMessage += `${error.message}. Please try again.`;
        statusError += error.message;
      }
      
      setStatusMessage(statusError);
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribeFromPushNotifications = async () => {
    setIsLoading(true);
    try {
      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();
        
        // Notify server
        await apiClient.post('/api/push/unsubscribe', {
          endpoint: subscription.endpoint
        });
      }
      
      setSubscription(null);
      setIsSubscribed(false);
      
      console.log('✅ Successfully unsubscribed from push notifications');
      alert('Push notifications disabled.');
      
    } catch (error) {
      console.error('Push unsubscription failed:', error);
      alert('Failed to disable push notifications. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const installApp = async () => {
    if (!installPrompt) return;
    
    // Show the install prompt
    installPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setIsInstallable(false);
      setInstallPrompt(null);
      
      // After installation, suggest enabling notifications
      setTimeout(() => {
        if (!isSubscribed) {
          const enableNotifications = window.confirm(
            'Great! Now would you like to enable push notifications to stay updated with schedule changes and announcements?'
          );
          if (enableNotifications) {
            subscribeToPushNotifications();
          }
        }
      }, 2000);
    } else {
      console.log('User dismissed the install prompt');
    }
  };

  // Helper function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  if (!user) {
    return null; // Don't show if user is not logged in
  }

  return (
    <div style={{ 
      margin: '20px 0', 
      padding: '15px', 
      border: '1px solid #ddd', 
      borderRadius: '8px',
      backgroundColor: '#f9f9f9'
    }}>
      <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>
        📱 App Installation & Notifications
      </h3>

      {/* Status Message */}
      {statusMessage && (
        <div style={{
          padding: '10px',
          marginBottom: '15px',
          backgroundColor: '#e8f4f8',
          border: '1px solid #b8dce8',
          borderRadius: '4px',
          fontSize: '14px',
          color: '#2c5282'
        }}>
          <strong>Status:</strong> {statusMessage}
        </div>
      )}
      

      
      {/* App Installation Section */}
      {(isInstallable || (isIOS && !isStandalone)) && (
        <div style={{ 
          marginBottom: '15px',
          padding: isIOS && !isStandalone ? '15px' : '0',
          backgroundColor: isIOS && !isStandalone ? '#e3f2fd' : 'transparent',
          border: isIOS && !isStandalone ? '2px solid #2196f3' : 'none',
          borderRadius: '8px'
        }}>
          <p style={{ 
            margin: '0 0 10px 0', 
            color: isIOS && !isStandalone ? '#1976d2' : '#666',
            fontWeight: isIOS && !isStandalone ? 'bold' : 'normal'
          }}>
            📲 {isIOS && !isStandalone ? 'Required for Notifications: ' : ''}Install this app on your device for quick access and better experience!
          </p>
          
          {isInstallable ? (
            <button
              onClick={installApp}
              style={{
                backgroundColor: '#2196f3',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                marginRight: '10px'
              }}
            >
              📱 Install {(process.env.REACT_APP_BRAND_SHORT_NAME || 'LightHouse')} App
            </button>
          ) : isIOS && !isStandalone ? (
            <div style={{ 
              padding: '10px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7', 
              borderRadius: '5px',
              fontSize: '12px'
            }}>
              <strong>To install on iPhone/iPad:</strong>
              <ol style={{ margin: '5px 0', paddingLeft: '20px' }}>
                <li>Tap the Share button (📤) in Safari</li>
                <li>Scroll down and tap "Add to Home Screen"</li>
                <li>Tap "Add" to confirm</li>
                <li>Open the app from your homescreen</li>
              </ol>
            </div>
          ) : null}
        </div>
      )}
      
      {/* Push Notifications Section */}
      {isSupported ? (
        <div>
          <p style={{ margin: '0 0 10px 0', color: '#666' }}>
            {isSubscribed 
              ? '🔔 Push notifications are enabled. You\'ll receive schedule updates and announcements.'
              : '🔕 Enable push notifications to receive schedule updates and important announcements instantly.'
            }
          </p>
          
          <button
            onClick={isSubscribed ? unsubscribeFromPushNotifications : subscribeToPushNotifications}
            disabled={isLoading}
            style={{
              backgroundColor: isSubscribed ? '#f44336' : '#4caf50',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading 
              ? '⏳ Processing...' 
              : (isSubscribed ? '🔕 Disable Notifications' : '🔔 Enable Notifications')
            }
          </button>
          
          {isSubscribed && (
            <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#888' }}>
              💡 Tip: You can also manage notifications in your browser settings.
            </p>
          )}
        </div>
      ) : (
        <div>
          {isIOS && !isStandalone ? (
            <div>
              <p style={{ margin: '0 0 10px 0', color: '#ff9800', fontWeight: 'bold' }}>
                📱 iOS Detected: Install App First!
              </p>
              <p style={{ margin: '0 0 10px 0', color: '#666' }}>
                On iPhone/iPad, push notifications only work after you install the app to your homescreen. 
                Please install the app first using the button above, then notifications will become available.
              </p>
              <div style={{ 
                padding: '10px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeaa7', 
                borderRadius: '5px',
                fontSize: '12px'
              }}>
                <strong>Steps:</strong>
                <ol style={{ margin: '5px 0', paddingLeft: '20px' }}>
                  <li>Tap "Install {(process.env.REACT_APP_BRAND_SHORT_NAME || 'LightHouse')} App" button above</li>
                  <li>Choose "Add to Home Screen"</li>
                  <li>Open the app from your homescreen</li>
                  <li>Push notifications will then be available</li>
                </ol>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ margin: '0', color: '#666' }}>
                ❌ Push notifications are not supported in this browser.
                Try using Chrome, Firefox, or Safari on a supported device.
              </p>
              {isIOS && (
                <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#888' }}>
                  Note: On iOS, you need iOS 16.4 or later for push notification support.
                </p>
              )}
            </div>
          )}
        </div>
      )}
      
      <div style={{ marginTop: '15px', fontSize: '12px', color: '#888' }}>
        <strong>What you'll receive notifications for:</strong>
        <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
          <li>Schedule assignments and changes</li>
          <li>Important announcements from officers</li>
          <li>Weather alerts and beach closures</li>
          <li>Schedule request deadlines</li>
        </ul>
        
        {isIOS && isStandalone && (
          <div style={{ 
            marginTop: '10px', 
            padding: '8px', 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #2196f3',
            borderRadius: '4px'
          }}>
            <strong>📱 iOS Troubleshooting:</strong>
            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
              <li>Make sure you're using iOS 16.4 or later</li>
              <li>Check Settings → Notifications → {process.env.REACT_APP_BRAND_SHORT_NAME || 'LightHouse'}</li>
              <li>Ensure "Allow Notifications" is enabled</li>
              <li>Try force-closing and reopening the app</li>
              <li>If it still doesn't work, check the browser console for errors</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PushNotificationManager; 